import React, { useState } from "react";
import { Save, User, Lock, ExternalLink } from "lucide-react";
import AdminLayout from "../../components/admin/AdminLayout";
import api from "../../utils/api";
import { useAuth } from "../../context/AuthContext";

export default function ProfilePage() {
  const { user, setUser } = useAuth();
  const [username, setUsername] = useState(user?.username || "");
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const inputCls = "w-full bg-zinc-900 border border-zinc-800 text-white rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:border-rose-600 transition-colors placeholder:text-zinc-600";
  const labelCls = "block text-zinc-400 text-xs font-bold uppercase tracking-wider mb-1.5";

  const handleSave = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;
    setSaving(true);
    setError("");
    setSuccess(false);
    try {
      const res = await api.put("/me/me", { username: username.trim() });
      setUser(prev => ({ ...prev, ...res.data }));
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
      <div className="max-w-xl">
        <div className="mb-6">
          <h1 className="text-2xl font-black text-white font-heading">Mein Profil</h1>
          <p className="text-zinc-500 text-sm mt-0.5">Anzeigename und Account-Einstellungen</p>
        </div>

        {/* Profil-Daten */}
        <form onSubmit={handleSave} className="bg-zinc-900 border border-white/5 rounded-xl p-5 space-y-4 mb-5">
          <div className="flex items-center gap-2 mb-1">
            <User size={14} className="text-zinc-400" />
            <h2 className="text-white font-bold text-sm">Profil-Daten</h2>
            {success && (
              <span className="ml-auto text-green-400 text-xs font-semibold flex items-center gap-1">
                <Save size={11} /> Gespeichert!
              </span>
            )}
          </div>
          {error && (
            <div className="text-red-400 text-sm bg-red-900/20 border border-red-500/20 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          <div>
            <label className={labelCls}>Anzeigename</label>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className={inputCls}
              required
              data-testid="profile-username"
            />
          </div>
          <div>
            <label className={labelCls}>E-Mail <span className="text-zinc-600 normal-case font-normal">(wird über Auth0 verwaltet)</span></label>
            <input
              value={user?.email || ""}
              readOnly
              className={`${inputCls} opacity-50 cursor-not-allowed`}
              data-testid="profile-email"
            />
          </div>
          <div className="flex items-center justify-between pt-1">
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold uppercase tracking-wide ${
              user?.role === "developer" ? "bg-purple-900/40 text-purple-400" : "bg-zinc-800 text-zinc-400"
            }`}>
              {user?.role}
            </span>
            <button
              type="submit"
              disabled={saving}
              data-testid="profile-save-btn"
              className="flex items-center gap-2 px-5 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white font-bold text-sm rounded-lg transition-colors"
            >
              <Save size={14} /> {saving ? "Speichern..." : "Speichern"}
            </button>
          </div>
        </form>

        {/* Auth0 Account-Verwaltung */}
        <div className="bg-zinc-900 border border-white/5 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Lock size={14} className="text-zinc-400" />
            <h2 className="text-white font-bold text-sm">Passwort &amp; E-Mail</h2>
          </div>
          <p className="text-zinc-500 text-sm mb-4">
            Passwort und E-Mail-Adresse werden über Auth0 verwaltet. Änderungen kannst du direkt im Auth0-Login-Bildschirm vornehmen.
          </p>
          <a
            href={`https://${process.env.REACT_APP_AUTH0_DOMAIN || "handsupwillneverdie.eu.auth0.com"}/u/reset-password`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-zinc-800 hover:bg-zinc-700 text-white font-semibold text-sm rounded-lg transition-colors border border-zinc-700"
          >
            <ExternalLink size={13} /> Passwort bei Auth0 zurücksetzen
          </a>
        </div>
      </div>
      </div>
    </AdminLayout>
  );
}
